export class ColorUtils {
    private static rgbToXYZ(R, G, B) {
        let
            var_R = parseFloat(R) / 255,   //R from 0 to 255
            var_G = parseFloat(G) / 255,   //G from 0 to 255
            var_B = parseFloat(B) / 255;   //B from 0 to 255


        if (var_R > 0.04045) {
            var_R = Math.pow(( ( var_R + 0.055 ) / 1.055 ), 2.4);
        } else {
            var_R = var_R / 12.92;
        }
        if (var_G > 0.04045) {
            var_G = Math.pow(( ( var_G + 0.055 ) / 1.055 ), 2.4);
        } else {
            var_G = var_G / 12.92;
        }
        if (var_B > 0.04045) {
            var_B = Math.pow(( ( var_B + 0.055 ) / 1.055 ), 2.4);
        } else {
            var_B = var_B / 12.92;
        }


        var_R = var_R * 100;
        var_G = var_G * 100;
        var_B = var_B * 100;

        //Observer. = 2°, Illuminant = D65
        return {
            x: var_R * 0.4124 + var_G * 0.3576 + var_B * 0.1805,
            y: var_R * 0.2126 + var_G * 0.7152 + var_B * 0.0722,
            z: var_R * 0.0193 + var_G * 0.1192 + var_B * 0.9505
        };

    }
    private static xyzToLAB(x, y, z) {

        const ref_X = 95.047,
            ref_Y = 100.000,
            ref_Z = 108.883;

        let var_X, var_Y, var_Z;

        var_X = x / ref_X;          //ref_X =  95.047   Observer= 2°, Illuminant= D65
        var_Y = y / ref_Y;          //ref_Y = 100.000
        var_Z = z / ref_Z;          //ref_Z = 108.883

        if (var_X > 0.008856) {
            var_X = Math.pow(var_X, ( 1 / 3 ));
        } else {
            var_X = ( 7.787 * var_X ) + ( 16 / 116 );
        }
        if (var_Y > 0.008856) {
            //noinspection JSSuspiciousNameCombination
            var_Y = Math.pow(var_Y, ( 1 / 3 ));
        } else {
            var_Y = ( 7.787 * var_Y ) + ( 16 / 116 );
        }
        if (var_Z > 0.008856) {
            var_Z = Math.pow(var_Z, ( 1 / 3 ));
        } else {
            var_Z = ( 7.787 * var_Z ) + ( 16 / 116 );
        }

        return {
            L: (116 * var_Y) - 16,
            a: 500 * (var_X - var_Y),
            b: 200 * (var_Y - var_Z)
        };
    }
    private static rgbObjectToLab(rgbObject:{r:number, g:number, b:number}) {

        const xyz = ColorUtils.rgbToXYZ(rgbObject.r, rgbObject.g, rgbObject.b);
        return ColorUtils.xyzToLAB(xyz.x, xyz.y, xyz.z);
    }

    public static getRGBObjectFromIntValue(color:number) {
        return ColorUtils.hexToRgbObject(ColorUtils.decToHex(color))
    }

    public static getDeltaEFrom2HexStrings(color1Hex:string, color2Hex:string) {
        if(color1Hex === "none" || color2Hex === "none") {
            return Infinity;
        }
        const lab1 = ColorUtils.rgbObjectToLab(ColorUtils.hexToRgbObject(color1Hex) as {r,g,b});
        const lab2 = ColorUtils.rgbObjectToLab(ColorUtils.hexToRgbObject(color2Hex) as {r,g,b});
        return Math.sqrt(Math.pow((lab1.L - lab2.L) / 2.55, 2) + Math.pow(lab1.a - lab2.a, 2) + Math.pow(lab1.b - lab2.b, 2));
    }


    /**
     * //Luminance Formel s. http://stackoverflow.com/questions/596216/formula-to-determine-brightness-of-rgb-color
     * @param {object.<r,g,b>}  rgbObject
     * @return {number}
     */
    public static getLuminanceOfRgbObject(rgbObject) {

        return 0.299 * rgbObject.r + 0.587 * rgbObject.g + 0.114 * rgbObject.b;
    }


    public static getColorWithMinDeviationFromPaletteInclMinData(colorIntValue, palette) {
        const colorHexString = ColorUtils.decToHex(colorIntValue);
        if(palette === null || typeof(palette.reduce) !== "function") {
            return  {colorHexValue:colorHexString, minDeltaE:Infinity};
        }
        return palette.reduce(function(carry, paletteEntry) {
            const deltaE = ColorUtils.getDeltaEFrom2HexStrings(colorHexString, paletteEntry.RGBHex);
            if(Math.abs(deltaE) < Math.abs(carry.minDeltaE)) {
                carry.colorHexValue = paletteEntry.RGBHex;
                carry.minDeltaE = deltaE;
                return carry;
            }
            return carry;
        }, {colorHexValue:"", minDeltaE:Infinity});

    }

    public static getColorWithMinDeviationFromPalette(colorIntValue, palette) {
        const minData = ColorUtils.getColorWithMinDeviationFromPaletteInclMinData(colorIntValue, palette);
        return ColorUtils.hexToDec(minData.colorHexValue);
    }


    public static getContrastColorIntToIntValue(colorValue, format?) {

        const colorValueHex = ColorUtils.decToHex(colorValue);
        return ColorUtils.hexToDec(ColorUtils.getContrastColorToHexValue(colorValueHex, format));
    }
    /**
     * GIbt schwarz oder weiß als Farbe zurück je nachdem, welche Farbe übergeben wurde
     * @param {string}  colorValue  als Hex oder RGB
     * @param {string}  [format]    "dec", "hex", "rgb"
     * @return {string|number|{r,g,b}|boolean}      Konstrastfarbe im angegebenen Format, standard: hex
     */
    public static getContrastColorToHexValue(colorValue, format="hex") {

        let rgbObject, out;

        if (colorValue.charAt(0) === '#') {
            rgbObject = ColorUtils.hexToRgbObject(colorValue.substring(1));
        } else {
            if (colorValue.charAt(0) === "r") {
                rgbObject = ColorUtils.rgbToRgbObject(colorValue);
            } else {
                rgbObject = ColorUtils.hexToRgbObject(colorValue);
            }
        }

        if (rgbObject === null) {
            return "#000000";
        }
        const luminance = ColorUtils.getLuminanceOfRgbObject(rgbObject);
        if (luminance > 179) {
            out = "#000000";
        } else {
            out = "#FFFFFF";
        }
        switch (format) {
            case "dec":
                return ColorUtils.hexToDec(out);
            case "rgb":
                return ColorUtils.hexToRgbObject(out);
            default:
                return out;
        }
    }
    /**
     * wandelt einen Hexstring in einen integerzahl um
     * @param {string}  hexValue    mit oder ohne führendes "#"
     * @return {number|boolean}
     */
    public static hexToDec(hexValue):number {
        if (typeof (hexValue) === "undefined"){
            return NaN;
        }
        if (hexValue.charAt(0) === '#') {
            hexValue = hexValue.substring(1);
        }
        const decimalValue = parseInt(hexValue, 16);
        if (isNaN(decimalValue)) {
            return NaN;
        }
        return decimalValue;
    }

    /**
     * wandelt eine Integerzahl in eine HexadezimalZahl */

    public static decToHex(decimalValue:number):string {

        return ColorUtils.padZero((decimalValue & 0x00ffffff).toString(16), 6);
    }

    public static decToHexWithPoundsign(decimalValue:any) {
        return "#"+ColorUtils.decToHex(decimalValue);
    }

    /**
     * wandelt einen 6 stelligen Hex Wert in einen rgb Wert um
     * @param {string}  hex     Wert der Farbe in Hexadecimal, mit oder ohne "#"
     * @return {{r,g,b}|null}
     */
    public static hexToRgbObject(hex:string) : {r:number, g:number, b:number}|null {

        if (hex.length < 6) {
            hex = ColorUtils.padZero(hex, 6);
        }
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        // Anfangs evtl ein "#", dann
        //3 Capture Gruppen a 2 Stellen mit A-F und Ziffern.
        //dann endet der String
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    }
    /**
     * zerlegt einen RGB String in seine Komponenten und liefert sie als Objekt zurück
     * @param {string} rgbStringValue
     * @return {object.<number,number,number>|null}
     */
    private static rgbToRgbObject(rgbStringValue) {

        const result = /rgb\((\d{1,3}) ?, ?(\d{1,3}) ?, ?(\d{1,3})\)/.exec(rgbStringValue);
        return result ? {
            r: parseInt(result[1], 10),
            g: parseInt(result[2], 10),
            b: parseInt(result[3], 10)
        } : null;
    }

    /**
     * füllt den string s von links mit Nullen auf, bis Länge len erreicht ist
     * @param {String}  s       Eingangs String
     * @param {number}  len     Länge des Zielstrings
     * @return {String}         String mit einer Länge von mindestens len (länger als len wenn Eingangsstring bereits länger)
     */
    private static padZero(s, len) {

        const c = '0';
        while (s.length < len) {
            s = c + s;
        }
        return s;
    }
}
